import { useState, useEffect, useCallback } from 'react'
import { Client as ConversationsClient } from '@twilio/conversations'
import { getAccessToken } from 'services/Authentication'

import { Net } from '../../services/Tools'

const MESSAGES_HISTORY_LIMIT = 25

const initialMessageList = {
  items: [],
  hasNextPage: false,
  hasPrevPage: false,
}

export default (token, conversationSid) => {
  const [client, setClient] = useState(null)
  const [loading, setLoading] = useState(true)
  const [messageList, setMessageList] = useState(initialMessageList)
  const [typing, setTyping] = useState(false)
  const [conversationClient, setConversationClient] = useState(null)
  const [conversation, setConversation] = useState(null)
  const accessToken = getAccessToken()

  const reset = useCallback(() => {
    setClient(null)
    setLoading(true)
    setMessageList(initialMessageList)
    setTyping(false)
    setConversationClient(null)
    setConversation(null)
  }, [])

  const refreshToken = useCallback(async () => {
    console.log('[Twilio-Conversations] Refreshing token... or not')
    // Implement your token refresh logic here if needed
  }, [])

  const addMessageToList = useCallback(newMessage => {
    setMessageList(({ items, ...v }) => ({
      items: [...items, newMessage],
      ...v,
    }))
  }, [])

  const handleTyping = useCallback(() => conversation?.typing(), [conversation])

  const handleTypingStarted = useCallback(
    participant => {
      if (participant.sid !== conversationClient?.user?.identity) {
        setTyping(true)
      }
    },
    [conversationClient?.user?.identity])

  const handleTypingEnded = useCallback(
    participant => {
      if (participant.sid !== conversationClient?.user?.identity) {
        setTyping(false)
      }
    },
    [conversationClient?.user?.identity])

  const handleLeaveConversation = useCallback(async () => {
    if (conversation) {
      try {
        await conversation.leave()
        conversation.removeListener('messageAdded', addMessageToList)
        conversation.removeListener('typingStarted', handleTypingStarted)
        conversation.removeListener('typingEnded', handleTypingEnded)
      } catch (e) {
        console.log('[Twilio-Conversations] Error leaving conversation: ', e)
      }
    }
    reset()
  }, [conversation, reset, addMessageToList, handleTypingStarted, handleTypingEnded])

  const setOldMessages = useCallback(
    async theConversation => {
      if (theConversation.sid === conversationSid) {
        try {
          const newMessages = await theConversation.getMessages(MESSAGES_HISTORY_LIMIT)
          if (newMessages.items.length) {
            setMessageList(oldMessages => ({
              ...newMessages,
              items: [...oldMessages.items, ...newMessages.items].sort((a, b) =>
                a.dateCreated < b.dateCreated ? -1 : 1
              ),
            }))
          }
        } catch (e) {
          console.log('[Twilio-Conversation] error fetching messages ', e)
        }
      }
    },
    [conversationSid]
  )

  const addParticipant = async (sid, log = true) => {
    try {
      const res = await Net.post(
        `${process.env.REACT_APP_CLINIC_SERVICE_URL}/addParticipant`,
        accessToken,
        { conversation_sid: sid },
        log
      )

      if (typeof res === 'number') return false
      return true
    } catch (err) {
      return false
    }
  }

  const getOrCreateConversation = useCallback(
    async (twilioClient, sid) => {
      const added = await addParticipant(sid)
      if (!added) throw new Error('Could not add participant to conversation')

      const existingConversation = await twilioClient.getConversationByUniqueName(sid)
      await setOldMessages(existingConversation)
      return existingConversation
    },
    [setOldMessages]
  )

  const handleSendMessage = useCallback(
    async newMessage => {
      const res = await conversation?.sendMessage(newMessage)
      return res
    },
    [conversation]
  )

  useEffect(() => {
    let isMounted = true

    if (token && conversationSid) {
      ;(async () => {
        try {
          const twilioClient = new ConversationsClient(token)
          setClient(twilioClient)

          twilioClient.on('tokenExpired', refreshToken)
          twilioClient.on('conversationJoined', setOldMessages)

          const newConversation = await getOrCreateConversation(twilioClient, conversationSid)

          newConversation.on('messageAdded', addMessageToList)
          newConversation.on('typingStarted', handleTypingStarted)
          newConversation.on('typingEnded', handleTypingEnded)

          if (isMounted) {
            setConversationClient(twilioClient)
            setConversation(newConversation)
            setLoading(false)
          }
        } catch (error) {
          console.error('[Twilio-Conversations] Error initializing client or joining conversation:', error)
          if (isMounted) setLoading(false)
        }
      })()
    }

    // Clean up
    return () => {
      isMounted = false
      client?.removeAllListeners()
      conversation?.removeAllListeners()
    }
  }, [token, conversationSid]) // Not having every item is wanted. Otherwise, it gets useless refreshes

  return {
    loading,
    messageList,
    typing,
    onTyping: handleTyping,
    sendMessage: handleSendMessage,
    conversationClient,
    conversation,
    close: handleLeaveConversation,
  }
}
